import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Navbar, Nav, Button } from 'react-bootstrap';
import StoreSelector from './components/StoreSelector';
import PriceComparison from './components/PriceComparison';
import ProductSearch from './components/ProductSearch';
import CheapProducts from './components/CheapProducts';

import StoreDetails from './components/StoreDetails';
import StoresBrowser from './components/StoresBrowser';
import Backoffice from './components/Backoffice';
import LocationPicker from './components/LocationPicker';
import { HomeLocationProvider, useHomeLocation } from './contexts/HomeLocationContext';
import { getAvailableStores } from './services/priceService';
import 'bootstrap/dist/css/bootstrap.min.css';

// Home component that contains the tabs
const Home = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'stores');
  const [initialBarcode, setInitialBarcode] = useState(() => searchParams.get('q') || '');

  useEffect(() => {
    const tab = searchParams.get('tab');
    const q = searchParams.get('q');
    if (tab) setActiveTab(tab);
    if (q !== null) setInitialBarcode(q);
  }, [searchParams]);
  const [selectedStores, setSelectedStores] = useState({
    store1Id: null,
    store2Id: null
  });
  const [storeNames, setStoreNames] = useState({});

  // Load store names for display
  useEffect(() => {
    const loadStoreNames = async () => {
      try {
        const stores = await getAvailableStores();
        const namesMap = {};
        stores.forEach(store => {
          namesMap[store.id] = store.name;
        });
        setStoreNames(namesMap);
      } catch (error) {
        console.error('Error loading store names:', error);
      }
    };

    loadStoreNames();
  }, []);

  const handleCompareClick = (store1Id, store2Id) => {
    console.log(`Comparing stores: ${store1Id} and ${store2Id}`);
    setSelectedStores({ store1Id, store2Id });
  };

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <h1 className="text-center mb-2" style={{ fontSize: 'var(--font-headline)', fontWeight: 600, letterSpacing: '-0.5px', color: 'var(--apple-text)' }}>Mehir — מחיר</h1>
          <p className="text-center mb-5" style={{ color: 'var(--apple-text)', fontSize: '17px' }}>
            Compare prices. Find the cheapest store near you.
          </p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col className="d-flex justify-content-center">
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'stores'}
                onClick={() => setActiveTab('stores')}
              >
                Browse Stores
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'compare'}
                onClick={() => setActiveTab('compare')}
              >
                Compare Stores
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'search'}
                onClick={() => setActiveTab('search')}
              >
                Find a Product
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={activeTab === 'deals'}
                onClick={() => setActiveTab('deals')}
              >
                Best Deals Nearby
              </Nav.Link>
            </Nav.Item>

          </Nav>
        </Col>
      </Row>

      <Row>
        <Col>
          {activeTab === 'compare' && (
            <>
              <Row>
                <Col>
                  <StoreSelector onCompareClick={handleCompareClick} />
                </Col>
              </Row>

              {selectedStores.store1Id && selectedStores.store2Id && (
                <Row className="mt-4">
                  <Col>
                    <PriceComparison
                      store1Id={selectedStores.store1Id}
                      store2Id={selectedStores.store2Id}
                      store1Name={storeNames[selectedStores.store1Id] || `Store ${selectedStores.store1Id}`}
                      store2Name={storeNames[selectedStores.store2Id] || `Store ${selectedStores.store2Id}`}
                    />
                  </Col>
                </Row>
              )}
            </>
          )}

          {activeTab === 'search' && (
            <ProductSearch initialBarcode={initialBarcode} />
          )}

          {activeTab === 'deals' && (
            <CheapProducts />
          )}

          {activeTab === 'stores' && (
            <StoresBrowser />
          )}

        </Col>
      </Row>
    </Container>
  );
};

// Location button shown in navbar
function LocationNavButton() {
  const { homeLocation } = useHomeLocation();
  const [showPicker, setShowPicker] = useState(false);
  return (
    <>
      <Button
        variant={homeLocation ? 'primary' : 'outline-secondary'}
        size="sm"
        onClick={() => setShowPicker(true)}
        title={homeLocation ? `Home: ${homeLocation.label}` : 'Set home location'}
        style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
        {homeLocation ? 'Location set' : 'Set location'}
      </Button>
      <LocationPicker show={showPicker} onHide={() => setShowPicker(false)} />
    </>
  );
}

// Footer with logo and last update time
function AppFooter() {
  const [lastUpdate, setLastUpdate] = React.useState(null);

  useEffect(() => {
    fetch('/api/last-update')
      .then(r => r.json())
      .then(data => setLastUpdate(data.lastUpdate))
      .catch(() => {});
  }, []);

  const formatted = lastUpdate
    ? new Date(lastUpdate).toLocaleString('he-IL', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <footer style={{
      borderTop: '1px solid rgba(0,0,0,0.08)',
      backgroundColor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      padding: '16px 0',
      marginTop: '48px',
    }}>
      <Container>
        <div className="d-flex align-items-center justify-content-center gap-2" style={{ color: '#6E6E73', fontSize: '13px' }}>
          <svg width="20" height="20" viewBox="0 0 28 28">
            <polygon points="26,14 20,4 8,4 2,14 8,24 20,24" fill="#15803D"/>
            <text x="14" y="19" textAnchor="middle" fill="white" fontSize="13" fontWeight="700" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif">₪</text>
          </svg>
          <span>Mehir</span>
          <span style={{ color: '#D1D1D6' }}>·</span>
          <span>מחירים מרשתות הסופרמרקט בישראל</span>
          {formatted && (
            <>
              <span style={{ color: '#D1D1D6' }}>·</span>
              <span>עדכון אחרון: {formatted}</span>
            </>
          )}
        </div>
      </Container>
    </footer>
  );
}

// Main App component with routing
function App() {
  return (
    <HomeLocationProvider>
    <Router>
      <div className="App">
        <Navbar
          expand="lg"
          variant="light"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 0,
            zIndex: 1030,
          }}
        >
          <Container>
            <Navbar.Brand as={Link} to="/" style={{ color: '#1D1D1F', fontWeight: 700, fontSize: '18px', letterSpacing: '-0.3px' }}>
              <svg width="30" height="30" viewBox="0 0 28 28" style={{ marginRight: 8, verticalAlign: 'middle' }}>
                <polygon points="26,14 20,4 8,4 2,14 8,24 20,24" fill="#15803D"/>
                <text x="14" y="19" textAnchor="middle" fill="white" fontSize="13" fontWeight="700" fontFamily="-apple-system, BlinkMacSystemFont, sans-serif">₪</text>
              </svg>
              Mehir
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="ms-auto align-items-center gap-2">
                <Nav.Link as={Link} to="/" style={{ color: '#6E6E73', fontSize: '15px' }}>Home</Nav.Link>
                <Nav.Link as={Link} to="/admin" style={{ color: '#6E6E73', fontSize: '15px' }}>Backoffice</Nav.Link>
                <LocationNavButton />
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>

        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/store/:storeId" element={<StoreDetails />} />
          <Route path="/admin" element={<Backoffice />} />
          <Route path="/compare/:storeId" element={<Navigate to="/" />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        <AppFooter />
      </div>
    </Router>
    </HomeLocationProvider>
  );
}

export default App;
